import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import {
  IAppState,
  IEmojiIcon,
  ImageModel,
  IMenu,
  IPage,
  IRoute,
  IShape,
  IVevImage,
  Variable,
  VevDispatch,
} from 'vev';
import { findParent, isString, root } from '../../utils';
import { raf } from '../../utils/animation';
import { loadPage } from '../../utils/route';
import { useContextModel } from '../context';
import { getState, StateContext } from '../state';
import { useGlobalStore } from './use-store';

export function usePages(): [IPage[], string | undefined] {
  return useGlobalStore((s) => [s.pages, s.dir]);
}

export function usePage(pageKey?: string): IPage | undefined {
  return useGlobalStore((s) => s.pages).find((page) => page.key === pageKey);
}

/**
 * `useFrame` can by use for animation or style changes.
 *  Use with caution as this will trigger the callback every animation frame
 *  Pass false as first argument to disabled.
 */
export function useFrame(
  callback: false | ((timestamp?: number) => void),
  deps?: ReadonlyArray<any>,
): void {
  useLayoutEffect(() => {
    if (callback === false) return;
    callback(performance.now());
    return raf(callback, true);
  }, deps);
}

/**
 * `useImage` watches and returns a give image with the given key
 */
export function useImage(imageKey: string): ImageModel | undefined {
  const images = useGlobalStore((state) => state.images);
  return images[imageKey];
}

/**
 * `useIsIndexPage` returns true if current page is index page
 */
export function useIsIndexPage(): boolean {
  return useGlobalStore((state) => {
    const currentPageKey = state?.route?.pageKey;
    const currentPage = state.pages.find((page) => page.key === currentPageKey);
    return currentPage?.index === true;
  });
}

/**
 * `useIcon` watches and returns the given icon,
 *  @param{iconKey} string - is the key of the icon you wish to get, icon keys can be found in the widgets manifest
 */
export function useIcon(
  iconKey: string | IShape | IEmojiIcon | IVevImage,
): IShape | string | undefined | IEmojiIcon | IVevImage {
  const model = useContextModel();
  const shapes = useGlobalStore((state) => state.shapes);
  if (!isString(iconKey)) return iconKey;
  if (model?.icons) return model.icons[iconKey];
  if (shapes?.[iconKey]) return shapes[iconKey];

  return [
    128,
    128,
    'M103 38.8L84 89.2h-8.6l-19-50.5h8l12.2 32.1 2.8 10.9h.5l2.8-10.9 12.2-32.1 8.1.1zM44 68.4H28v20.8h16.4V85H32.9v-4.5h10.3v-4.2H32.9v-3.5H44v-4.4zm-7.9-12.7l-1.2-4.5-4.4-12.5H25l7.5 20.8h7.3l7.5-20.8h-5.5l-4.4 12.5-1.2 4.5h-.1z',
  ] as IShape;
}

const emptyMenu = { children: [], title: '' };

/**
 * `useMenu` watches and returns the given menu
 * if no menu key defined the primary menu will be used
 */
export function useMenu(menuKey?: string): IMenu {
  return useGlobalStore(
    ({ menus, primaryMenu }) => {
      const key = menuKey || primaryMenu;
      return (menus && key && menus[key]) || emptyMenu;
    },
    [menuKey],
  );
}

type VirtualModelContext = any;
export const VirtualModelContext = createContext<VirtualModelContext | undefined>(undefined);
/**
 * `useModel` watches and returns the current or given content model
 */
export const useModel = useContextModel;

/**
 * `useScrollTop` watches and returns the current scroll top
 * if percentage argument is true the scroll progress is return as float between [0,1]
 */
export function useScrollTop(percentage?: boolean): number {
  return useGlobalStore(
    ({ scrollTop, viewport }) =>
      percentage
        ? viewport.scrollHeight <= viewport.height
          ? 1
          : Math.max(0, scrollTop / (viewport.scrollHeight - viewport.height))
        : scrollTop,
    [percentage],
  );
}

/**
 * `useDevice` watches and returns the id of the current device
 */
export function useDevice(): string {
  return useGlobalStore((state) => state.device, []);
}

/**
 * `useMainDevice` watches and returns the id of the current main device
 */
export function useMainDevice(): string {
  return useGlobalStore((state) => state.masterMode, []);
}

/**
 * `useViewport` watches and returns the viewport (window width,height, and scrollHeight)
 */
export function useViewport(): { width: number; height: number; scrollHeight: number } {
  return useGlobalStore((state) => state.viewport, []);
}

/**
 * `useZoom` watches and returns the zoom amount
 */
export function useZoom(): number {
  return useGlobalStore(({ zoom }) => zoom, []);
}

/**
 * `useRoute` watches and returns for changes to the current route
 */
export function useRoute(): IRoute {
  return useGlobalStore((state) => state.route, []);
}

/**
 * `useLiveEvent` can be used to watch all events on a give tag of a certain type happening inside a given element
 * @example useLiveEvent('a', 'click', document.body, () => console.log('LINK CLICKED'));
 */
export function useLiveEvent<K extends keyof HTMLElementEventMap>(
  tagname: string,
  eventName: K,
  rootEl: React.RefObject<HTMLElement | undefined> | HTMLElement = root,
  callback: (ev: HTMLElementEventMap[K], source: HTMLElement) => any,
  deps?: any[],
) {
  useEffect(() => {
    const el = rootEl instanceof Element ? rootEl : rootEl.current;

    const handler = (e: HTMLElementEventMap[K]) => {
      const target = findParent(tagname.toUpperCase(), (e.target || e.srcElement) as HTMLElement);
      if (target) callback(e, target);
    };

    if (el) {
      el.addEventListener(eventName, handler);
    }
    return () => {
      if (el) {
        el.removeEventListener(eventName, handler);
      }
    };
  }, deps);
}

/**
 * `usePreloadPage` will preload a page for faster navigation
 * @example
 *    usePreloadPage(<pageKey>);
 */
export function usePreloadPage(pageKeys: string | string[]) {
  const [stateRef, dispatch] = useGlobalStateRef();

  useEffect(() => {
    if (Array.isArray(pageKeys)) {
      pageKeys.forEach((pageKey) => {
        loadPage(pageKey, stateRef.current, dispatch);
      });
    } else {
      loadPage(pageKeys, stateRef.current, dispatch);
    }
  }, [dispatch, pageKeys, stateRef]);
}

/**
 * `useGlobalStateRef` returns a mutable ref to the global IAppState for the current context
 * Note that `useGlobalStateRef()` It will not trigger a render when app state changes,
 * but it can be used to improve the performance of components because you'll have access to the latest version of the state in all useEffects or useFrames
 * without needing to re-render your component
 */
export function useGlobalStateRef(): [React.MutableRefObject<IAppState>, VevDispatch] {
  const [listen, dispatch, uid] = useContext(StateContext);
  const state = useRef<IAppState>(getState(uid));

  useEffect(() => listen((s) => (state.current = s)), []);
  return [state, dispatch];
}

export function useStorage(
  itemKey: string,
  scope: 'account' | 'workspace' | 'project' = 'project',
) {
  const model = useContextModel();

  return useGlobalStore(({ widgetStorage }) => {
    if (!model?.type) return undefined;
    return widgetStorage?.[scope || 'project']?.[model.type]?.[itemKey];
  }, []);
}

export const useGlobalState = (key?: string, pkgKey?: string) => {
  const a = useRef<any>();
  const model = useModel();

  const globalStateKey = `${pkgKey || model?.type}__${key || model?.key}`;

  return useGlobalStore((store, dispatch) => {
    if (!a.current) a.current = (state: any) => dispatch('widget-state', state, globalStateKey);
    return [store?.widgetState?.[globalStateKey], a.current];
  });
};
